import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { useSelector } from "react-redux";
import { FeatherIcon } from "component/shared/feather-icon";
import { PhotoUploadDialogButton } from "component/shared/photo-upload-dialog-button";
import { RootState, useAppThunkDispatch } from "redux/store";
import {
	getListingPhotosOrdered,
	getListingPhotosFlags,
	getListingPhotosLoading,
	getListingPhotosLoaded,
} from "redux/selector";
import {
	fetchListingPhotos,
	addExtraPhoto,
	addAsMainPhoto,
	reorderPhotos,
	deletePhoto,
	makeMainPhoto,
	revertPhotosToMls,
	listingPhotosReset,
} from "redux/slice/listing-photos";
import { ListingPhotoKey, PhotoResource } from "api/resource/listing-photo";
import { Upload } from "model/upload";
import { styles } from "./style";

// CF had no upload size cap for listing photos; the shared upload widget's 600KB default is far too
// small for real-estate photography, so this raises it per-caller rather than globally (MAX-3898).
const LISTING_PHOTO_MAX_FILE_SIZE = 25 * 1024 * 1024;

interface Props {
	listingKey: ListingPhotoKey;
	hasBoard: boolean;
	label?: string;
	// When true, show an inline row of photo thumbnails above the button (fetched on mount) rather than
	// only revealing photos inside the dialog.
	preview?: boolean;
	// When true, the inline preview shows only the main photo (MAX-3924). The API still returns the full
	// set (available in the manage dialog / for a future redesign) — this just conforms the at-a-glance
	// display to CF, which shows a single main photo for active MLS listings rather than the whole tour.
	mainPhotoOnly?: boolean;
	// MAX-3939: render the manager as an INLINE panel (for the editor "Photos" tab, mirroring CF's
	// separate Photos tab) rather than a button that opens a dialog. Shows the upload controls, the main
	// photo, and the FULL photo list directly — this is what fixes "the page only shows the main photo".
	// Fetches on mount / resets on unmount, like `preview`.
	inline?: boolean;
}

// Stable string for the listingKey union so effect deps don't churn on the new object literal every
// render — every call site passes an inline `{ clientPropertyId: id }`.
const keyToString = (key: ListingPhotoKey): string =>
	"clientPropertyId" in key ? `cp:${key.clientPropertyId}` : `ln:${key.listingNumber}:${key.boardId}`;

const useStyles = makeStyles()(styles);

export const PhotoManager: React.FC<Props> = ({
	listingKey,
	hasBoard,
	label,
	preview,
	mainPhotoOnly,
	inline,
}) => {
	const { classes } = useStyles();
	const dispatch = useAppThunkDispatch();

	const photos = useSelector(getListingPhotosOrdered);
	const flags = useSelector((state: RootState) => getListingPhotosFlags(state));
	const loading = useSelector(getListingPhotosLoading);
	const loaded = useSelector(getListingPhotosLoaded);

	const [open, setOpen] = useState(false);
	const [makeMainOnAdd, setMakeMainOnAdd] = useState(false);
	const [toDelete, setToDelete] = useState<PhotoResource | null>(null);
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
		open: false,
		message: "",
		severity: "success",
	});

	const keyString = keyToString(listingKey);

	useEffect(() => {
		if (open) {
			dispatch(fetchListingPhotos({ key: listingKey }));
		}
		// eslint-disable-next-line
	}, [open, keyString]);

	// Inline panel / preview strip: load this listing's photos on mount and keep them in the shared slice.
	// Reset on unmount / key change so one listing's photos don't leak into the next editor that reuses the
	// slice.
	useEffect(() => {
		if (!preview && !inline) {
			return;
		}
		dispatch(fetchListingPhotos({ key: listingKey }));
		return () => {
			dispatch(listingPhotosReset());
		};
		// eslint-disable-next-line
	}, [preview, inline, keyString]);

	const notify = (message: string, severity: "success" | "error" = "success") =>
		setSnackbar({ open: true, message, severity });

	const handleClose = () => {
		setOpen(false);
		setMakeMainOnAdd(false);
		// Clear state on close so a different listing's photos never leak into the next open — this
		// component instance is reused per-listing-editor, but the slice is a single shared snapshot.
		// In preview/inline mode the mount effect owns the slice lifecycle (fetch on mount / reset on
		// unmount), so leave it populated here and let dialog edits stay reflected.
		if (!preview && !inline) {
			dispatch(listingPhotosReset());
		}
	};

	const handleUpload = async (files: Upload[]) => {
		for (const file of files) {
			try {
				if (makeMainOnAdd) {
					await dispatch(addAsMainPhoto({ key: listingKey, photoUrl: file.url })).unwrap();
				} else {
					await dispatch(addExtraPhoto({ key: listingKey, photoUrl: file.url })).unwrap();
				}
			} catch{
				notify("Failed to add photo", "error");
			}
		}
	};

	const handleMove = async (photo: PhotoResource, direction: -1 | 1) => {
		const index = photos.findIndex((p) => p.propertyPhotoId === photo.propertyPhotoId);
		const swapWith = index + direction;
		if (index < 0 || swapWith < 0 || swapWith >= photos.length) {
			return;
		}
		const reordered = [...photos];
		[reordered[index], reordered[swapWith]] = [reordered[swapWith], reordered[index]];
		try {
			await dispatch(
				reorderPhotos({ key: listingKey, orderedIds: reordered.map((p) => p.propertyPhotoId) })
			).unwrap();
		} catch{
			notify("Failed to reorder photos", "error");
		}
	};

	const handleMakeMain = async (photo: PhotoResource) => {
		try {
			await dispatch(makeMainPhoto({ key: listingKey, propertyPhotoId: photo.propertyPhotoId })).unwrap();
		} catch{
			notify("Failed to set main photo", "error");
		}
	};

	const handleDelete = async () => {
		if (!toDelete) {
			return;
		}
		const photo = toDelete;
		setToDelete(null);
		try {
			await dispatch(deletePhoto({ key: listingKey, propertyPhotoId: photo.propertyPhotoId })).unwrap();
		} catch{
			notify("Failed to delete photo", "error");
		}
	};

	const handleRevert = async () => {
		try {
			await dispatch(revertPhotosToMls({ key: listingKey })).unwrap();
			notify("Photos reverted to MLS");
		} catch{
			notify("Failed to revert photos to MLS", "error");
		}
	};

	// The manager panel (upload controls + main photo + full photo list) — shared between the inline
	// "Photos tab" layout (MAX-3939) and the legacy dialog.
	const panelContent =
		loading && !loaded ? (
			<Mui.CircularProgress />
		) : (
			<>
				<div className={classes.uploadRow}>
					<PhotoUploadDialogButton
						label="Upload Photos"
						variant="outlined"
						color="primary"
						icon={<Icons.Upload />}
						/* MAX-3939: single photo per upload (Cloudinary multiple:false, matching CF) — the
						   "Make Main Photo on add" checkbox is only coherent one-at-a-time. */
						maxFileSize={LISTING_PHOTO_MAX_FILE_SIZE}
						/* MAX-3930 (D-PM-6): harden the widget to images from local/url only,
						   matching CF's Cloudinary config (phototourmodify_output_1.cfm:16-23). */
						resourceType="image"
						clientAllowedFormats={["png", "jpeg", "jpg"]}
						sources={["local", "url"]}
						callback={handleUpload}
					/>
					<Mui.FormControlLabel
						control={
							<Mui.Checkbox
								color="primary"
								checked={makeMainOnAdd}
								onChange={(e) => setMakeMainOnAdd(e.target.checked)}
								data-testid="photo-manager-make-main-on-add"
							/>
						}
						label="Make Main Photo on add"
					/>
					{/* Parity: the backend computes canRevertToMls = hasBoard && photoYn (CF's
						boardID!=0 AND photoYN==1 rule), so gate on the fetched flag rather than the
						static hasBoard prop — Revert must hide once the listing's main photo isn't a
						custom upload, even when hasBoard is true. Before the first fetch resolves
						there's no fetched flag yet, so fall back to the hasBoard prop. */}
					{(loaded ? flags.canRevertToMls : hasBoard) && (
						<Mui.Button variant="outlined" onClick={handleRevert} data-testid="photo-manager-revert">
							Revert to MLS
						</Mui.Button>
					)}
				</div>

				{/* Main Photo block always renders from the scalar (MLS main or custom main), separate from
					the list — matching CF's Photos tab, which always shows a Main Photo thumbnail. */}
				{flags.mainPhotoUrl && (
					<div>
						<Mui.Typography variant="subtitle2" gutterBottom>
							Main Photo
						</Mui.Typography>
						<div className={classes.photoCard}>
							<img
								src={flags.mainPhotoUrl}
								alt=""
								className={classes.thumbnail}
								data-testid="photo-manager-main-photo"
							/>
						</div>
					</div>
				)}

				{/* Additional Photos: the FULL list of custom photos (CF's "Additional Photos" section,
					shown when recordcount > 0). This is what MAX-3939 restores — the editor previously hid
					this behind a dialog and only showed the main photo. */}
				{photos.length > 0 ? (
					<div>
						<Mui.Typography variant="subtitle2" gutterBottom>
							Additional Photos
						</Mui.Typography>
						<div className={classes.gallery}>
							{photos.map((photo, index) => {
								const isMain = !!flags.mainPhotoUrl && photo.photoUrl === flags.mainPhotoUrl;
								return (
									<div
										key={photo.propertyPhotoId}
										className={classes.photoCard}
										data-testid={`photo-manager-photo-${photo.propertyPhotoId}`}
									>
										{isMain && (
											<Mui.Chip
												size="small"
												color="primary"
												label="Main"
												className={classes.mainChip}
											/>
										)}
										<img src={photo.photoUrl} alt="" className={classes.thumbnail} />
										<div className={classes.photoActions}>
											<div>
												<Mui.IconButton
													size="small"
													disabled={index === 0}
													onClick={() => handleMove(photo, -1)}
													data-testid={`photo-manager-move-up-${photo.propertyPhotoId}`}
												>
													<FeatherIcon fontSize="small">
														<Icons.ArrowUp />
													</FeatherIcon>
												</Mui.IconButton>
												<Mui.IconButton
													size="small"
													disabled={index === photos.length - 1}
													onClick={() => handleMove(photo, 1)}
													data-testid={`photo-manager-move-down-${photo.propertyPhotoId}`}
												>
													<FeatherIcon fontSize="small">
														<Icons.ArrowDown />
													</FeatherIcon>
												</Mui.IconButton>
											</div>
											<div>
												<Mui.Tooltip title="Make main photo">
													<Mui.IconButton
														size="small"
														disabled={isMain}
														onClick={() => handleMakeMain(photo)}
														data-testid={`photo-manager-make-main-${photo.propertyPhotoId}`}
													>
														<FeatherIcon fontSize="small">
															<Icons.Star />
														</FeatherIcon>
													</Mui.IconButton>
												</Mui.Tooltip>
												<Mui.IconButton
													size="small"
													onClick={() => setToDelete(photo)}
													data-testid={`photo-manager-delete-${photo.propertyPhotoId}`}
												>
													<FeatherIcon fontSize="small">
														<Icons.Trash2 />
													</FeatherIcon>
												</Mui.IconButton>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				) : (
					!flags.mainPhotoUrl && (
						<Mui.Typography variant="body2" color="textSecondary" className={classes.emptyState}>
							No photos yet.
						</Mui.Typography>
					)
				)}
			</>
		);

	const deleteConfirm = (
		<Mui.Dialog open={!!toDelete} onClose={() => setToDelete(null)}>
			<Mui.DialogTitle>Delete Photo</Mui.DialogTitle>
			<Mui.DialogContent>
				<Mui.DialogContentText>Are you sure you want to delete this photo?</Mui.DialogContentText>
			</Mui.DialogContent>
			<Mui.DialogActions>
				<Mui.Button onClick={() => setToDelete(null)} variant="outlined" color="primary">
					No
				</Mui.Button>
				<Mui.Button
					onClick={handleDelete}
					variant="contained"
					color="primary"
					data-testid="photo-manager-delete-confirm"
				>
					Yes
				</Mui.Button>
			</Mui.DialogActions>
		</Mui.Dialog>
	);

	const snackbarEl = (
		<Mui.Snackbar
			open={snackbar.open}
			autoHideDuration={4000}
			onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
		>
			<MuiAlert severity={snackbar.severity}>{snackbar.message}</MuiAlert>
		</Mui.Snackbar>
	);

	// MAX-3939: inline panel for the editor "Photos" tab — no open button, no dialog wrapper.
	if (inline) {
		return (
			<div data-testid="photo-manager-inline">
				{panelContent}
				{deleteConfirm}
				{snackbarEl}
			</div>
		);
	}

	return (
		<>
			{preview && mainPhotoOnly && (() => {
				// Conform to CF: show only the main photo (MAX-3924). Prefer the resource's mainPhotoUrl
				// (MLS main, or a custom main override); fall back to the first photo if it isn't set.
				const mainUrl = flags.mainPhotoUrl || (photos.length > 0 ? photos[0].photoUrl : "");
				return mainUrl ? (
					<div className={classes.previewStrip} data-testid="photo-manager-preview">
						<img src={mainUrl} alt="" className={classes.previewThumb} />
					</div>
				) : null;
			})()}

			{preview && !mainPhotoOnly && photos.length > 0 && (
				<div className={classes.previewStrip} data-testid="photo-manager-preview">
					{photos.map((photo) => (
						<img key={photo.propertyPhotoId} src={photo.photoUrl} alt="" className={classes.previewThumb} />
					))}
				</div>
			)}

			<Mui.Button variant="outlined" onClick={() => setOpen(true)} data-testid="photo-manager-open">
				{label || "Add / Manage Photos"}
			</Mui.Button>

			<Mui.Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth data-testid="photo-manager-dialog">
				<Mui.DialogTitle className={classes.dialogTitle}>
					<Mui.Typography variant="h6">Manage Photos</Mui.Typography>
					<Mui.IconButton onClick={handleClose} size="small" data-testid="photo-manager-close">
						<FeatherIcon>
							<Icons.X />
						</FeatherIcon>
					</Mui.IconButton>
				</Mui.DialogTitle>
				<Mui.DialogContent dividers>{panelContent}</Mui.DialogContent>
				<Mui.DialogActions>
					<Mui.Button onClick={handleClose} variant="outlined">
						Close
					</Mui.Button>
				</Mui.DialogActions>
			</Mui.Dialog>

			{deleteConfirm}
			{snackbarEl}
		</>
	);
};
